import React from "react";
import { Lock } from "@phosphor-icons/react";

const matches = (f, q) => !q || f.label.toLowerCase().includes(q.toLowerCase()) || f.key.includes(q.toLowerCase());

function FieldRow({ f, sent, locked, name, ph, onToggle, onRename }) {
  return (
    <div className="flex items-center gap-2 py-1" data-testid={`lead-share-row-${f.key}`}>
      <label className={`flex min-w-0 flex-1 items-center gap-2 text-sm ${locked ? "" : "cursor-pointer"}`}>
        {locked ? <Lock size={13} className="shrink-0 text-zinc-500" /> : (
          <input type="checkbox" className="accent-black" checked={sent} onChange={() => onToggle(f.key)} data-testid={`lead-share-field-${f.key}`} />
        )}
        <span className={`truncate ${sent ? "" : "text-zinc-400 line-through"}`} title={f.label}>{f.label}</span>
      </label>
      <input value={name || ""} placeholder={ph} disabled={!sent} onChange={(e) => onRename(f.key, e.target.value)} maxLength={80}
             aria-label={`Name in the buyer's system for ${f.label}`} data-testid={`lead-share-name-${f.key}`}
             className={`w-32 shrink-0 border px-1.5 py-0.5 font-mono text-[11px] transition-colors duration-100 placeholder:text-zinc-400 focus:border-black focus:outline-none disabled:bg-zinc-100 disabled:opacity-50 sm:w-40 ${name ? "border-black bg-yellow-100" : "border-zinc-400 bg-white"}`} />
    </div>
  );
}

function FieldGroup({ g, fields, locked, rule, defNames, onToggle, onRename, onGroup }) {
  const sent = g.fields.filter((f) => !rule.hidden.includes(f.key)).length;
  return (
    <div className="mb-4" data-testid={`lead-share-group-${g.key}`}>
      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-zinc-300 pb-1">
        <div className="label-mono">{g.label}</div>
        {locked ? <span className="text-[10px] text-zinc-500" data-testid="lead-share-always">Sent on every lead — you can only rename these</span> : (
          <>
            <span className="text-[10px] text-zinc-500" data-testid={`lead-share-group-count-${g.key}`}>{sent} of {g.fields.length} sent</span>
            <div className="ml-auto flex gap-2 text-[10px] uppercase tracking-widest">
              <button type="button" className="underline" onClick={() => onGroup(g, true)} data-testid={`lead-share-all-${g.key}`}>Send all</button>
              <button type="button" className="underline" onClick={() => onGroup(g, false)} data-testid={`lead-share-none-${g.key}`}>Send none</button>
            </div>
          </>
        )}
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-x-6 xl:grid-cols-2">
        {fields.map((f) => (
          <FieldRow key={f.key} f={f} locked={locked} sent={locked || !rule.hidden.includes(f.key)} name={rule.names[f.key]}
                    ph={defNames[f.key] || f.key} onToggle={onToggle} onRename={onRename} />
        ))}
      </div>
    </div>
  );
}

// Tick boxes plus, next to each field, the name the buyer's system should get.
export function FieldGroups({ cat, rule, defNames, q, onToggle, onRename, onGroup }) {
  const all = [{ g: { key: "always", label: "Always sent", fields: cat.always_fields }, locked: true },
    ...cat.share_groups.map((g) => ({ g, locked: false }))];
  const groups = all.map((x) => ({ ...x, fields: x.g.fields.filter((f) => matches(f, q)) })).filter((x) => x.fields.length);
  return (
    <>
      <p className="mb-3 text-[11px] text-zinc-600" data-testid="lead-share-names-hint">
        <b>Their field name:</b> type a new name in the box next to a field (for example <code>Phone1</code>) and web posts and file drops send it under that name. Leave it blank to keep the name in grey. CRMs keep their own matching; lead emails show readable labels.
      </p>
      {groups.length === 0 && <p className="py-3 text-sm" data-testid="lead-share-no-match">No fields match &ldquo;{q}&rdquo;.</p>}
      {groups.map(({ g, fields, locked }) => (
        <FieldGroup key={g.key} g={g} fields={fields} locked={locked} rule={rule} defNames={defNames}
                    onToggle={onToggle} onRename={onRename} onGroup={onGroup} />
      ))}
    </>
  );
}
